
import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { UseSpeechRecognitionProps, UseSpeechRecognitionReturn } from './types';
import { useWebSpeechRecognition } from './webSpeechRecognition';
import { useNativeSpeechRecognition } from './nativeSpeechRecognition';

export const useSpeechRecognition = ({ 
  onTranscriptionComplete, 
  language = 'en-US' 
}: UseSpeechRecognitionProps): UseSpeechRecognitionReturn => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<any>(null);
  const langRef = useRef<string>(language);
  
  const webSpeech = useWebSpeechRecognition({
    onTranscriptionComplete,
    language: langRef.current,
    isListening,
    setIsListening
  });
  
  const nativeSpeech = useNativeSpeechRecognition({
    onTranscriptionComplete,
    language: langRef.current,
    isListening,
    setIsListening
  });

  // Helper to determine if we should use web speech (for iOS + web)
  // iOS uses web speech (Gladia API), Android uses native plugin
  const shouldUseWebSpeech = () => {
    const platform = Capacitor.getPlatform();
    return platform === 'web' || platform === 'ios';
  };

  // Update language reference when it changes
  useEffect(() => {
    langRef.current = language;
    if (recognitionRef.current && shouldUseWebSpeech()) {
      recognitionRef.current.lang = language;
    }
  }, [language]);

  // Check if speech recognition is available
  useEffect(() => {
    const checkAvailability = async () => {
      const platform = Capacitor.getPlatform();
      
      if (platform === 'android') {
        // Android uses native speech recognition
        const available = await nativeSpeech.isSupported();
        setIsSupported(available);
      } else {
        // iOS and web use web speech (which connects to Gladia API)
        setIsSupported(webSpeech.isSupported());
      }
    };
    
    checkAvailability();
  }, []);

  // Initialize web speech recognition for iOS and web platforms
  useEffect(() => {
    if (shouldUseWebSpeech()) {
      recognitionRef.current = webSpeech.createRecognition();
    }
    
    return () => {
      if (recognitionRef.current && shouldUseWebSpeech()) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error('Error stopping recognition on cleanup:', error);
        }
      }
    };
  }, []);

  const toggleListening = async () => {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'android') {
      // Android uses native speech recognition
      if (isListening) {
        await nativeSpeech.stopRecognition();
      } else {
        await nativeSpeech.startRecognition();
      }
    } else {
      // iOS and web use web speech recognition (Gladia API)
      if (!recognitionRef.current) {
        recognitionRef.current = webSpeech.createRecognition();
      }
      
      if (isListening) {
        webSpeech.stopRecognition(recognitionRef.current);
      } else {
        webSpeech.startRecognition(recognitionRef.current);
      }
    }
  };

  return {
    isListening,
    toggleListening,
    isSupported
  };
};
